import {
  Icon,
  List,
  useCallbackRef,
  useNavigationContext,
} from '@infotech-soe/erp-ui-kit';
import React, {
  forwardRef,
  memo,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTextOverflowed } from '../../hooks/useTextOverflowed';

import { DEFAULT_GROUP_ICON, MENU_ROOT_ICON_CLASS } from './constants';
import { useMenuContext } from './MenuContext';
import MenuItems from './MenuItems';
import { useGroupItemStyles } from './styles';
import { GroupMenuItemProps } from './types';
import WithTooltip from './WithTooltip';

const GroupMenuItem = (
  props: GroupMenuItemProps,
  ref: Ref<HTMLDivElement>
): React.JSX.Element => {
  const { children, icon: iconProp, id, label, menuNestinglevel = 0 } = props;
  const classes = useGroupItemStyles(menuNestinglevel);
  const [show, setShow] = useState(false);

  const { active, onActivate, onRegister } = useNavigationContext();
  const { isOverflowed, textRef } = useTextOverflowed();
  const {
    collapsed,
    currentOpenedMenuId,
    rootMenuIds,
    onClickOnGroupItem,
    onToggleMenu,
  } = useMenuContext();

  const isActive = useMemo(() => active === id, [active, id]);

  const handleClick = useCallback(() => {
    if (rootMenuIds.includes(id)) onClickOnGroupItem(id);
    collapsed ? onToggleMenu(true) : setShow(!show);
  }, [collapsed, id, onClickOnGroupItem, onToggleMenu, rootMenuIds, show]);

  const callbackRef = useCallbackRef(
    (el: HTMLElement | null) => onRegister(el, id),
    ref
  );

  useEffect(() => {
    setShow(currentOpenedMenuId === id);
  }, [currentOpenedMenuId, id]);

  const icon = useMemo(
    () => iconProp || (menuNestinglevel === 0 ? DEFAULT_GROUP_ICON : undefined),
    [iconProp, menuNestinglevel]
  );

  return (
    <>
      <List.Header
        className={classes.listHeader}
        focusable
        isActive={isActive}
        name={id}
        ref={callbackRef}
        tabIndex={isActive ? 0 : -1}
        onActivate={onActivate}
        onClick={handleClick}
      >
        {icon && menuNestinglevel === 0 && (
          <Icon className={MENU_ROOT_ICON_CLASS} name={icon} />
        )}
        <Icon
          className={classes.arrow}
          name={show ? 'down-arrow' : 'right-arrow'}
          size={16}
        />
        <WithTooltip condition={isOverflowed} label={label} delay={300}>
          <div className={classes.label} ref={textRef}>
            {label}
          </div>
        </WithTooltip>
      </List.Header>
      {collapsed ? null : <MenuItems show={show} items={children} />}
    </>
  );
};

export default memo(forwardRef(GroupMenuItem));
